import clsx from "clsx";
import type { KeyboardEvent, TextareaHTMLAttributes } from "react";
import { forwardRef } from "react";
import { Mention, MentionsInput } from "react-mentions";

import { Field } from "../Input";
import { Label } from "../Label";

type Suggestion = {
  count: number;
  display: string;
  id: string;
};

type TextAreaWithMentionsProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  invalid?: boolean;
  label?: string;
  onChange?: (value: string) => void;
  onKeyDown?: (
    event: KeyboardEvent<HTMLInputElement> | KeyboardEvent<HTMLTextAreaElement>,
  ) => void;
  suggestions?: Suggestion[];
  toolTipText?: string;
};

const mentionInputStyle = {
  control: {
    backgroundColor: "#fff",
    fontSize: 14,
    fontWeight: "normal",
    minHeight: "40px",
  },
  highlighter: {
    // Must stay in sync with `input` below — the highlighter is an
    // absolutely-positioned overlay behind the real textarea, and if the two
    // don't share the same height/overflow, they scroll out of alignment.
    overflowY: "auto" as const,
    padding: "9px 12px",
  },
  input: {
    backgroundColor: "white",
    border: "1px solid #D1D5DB",
    borderRadius: "6px",
    color: "#111827",
    fontSize: "14px",
    minHeight: "40px",
    // Cap growth and scroll internally instead of letting long content push
    // the box taller than its container and overlap whatever's below it.
    overflowY: "auto" as const,
    padding: "9px 12px",
  },
  suggestions: {
    item: {
      "&focused": {
        backgroundColor: "#EFF6FF",
      },
      backgroundColor: "white",
      borderBottom: "1px solid rgba(0,0,0,0.15)",
      cursor: "pointer",
      padding: "8px 12px",
    },
    list: {
      backgroundColor: "white",
      border: "1px solid rgba(0,0,0,0.15)",
      borderRadius: "6px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      fontSize: 14,
      left: 0,
      maxHeight: "200px",
      overflow: "auto",
      position: "absolute",
      right: 0,
      top: "100%",
      width: "200px",
      zIndex: 9999,
    },
  },
};

const TextAreaWithMentions = forwardRef<
  HTMLTextAreaElement,
  TextAreaWithMentionsProps
>(
  (
    {
      className,
      label,
      toolTipText,
      invalid,
      onChange,
      onKeyDown,
      required,
      disabled,
      rows = 1,
      suggestions = [],
      value,
      ...props
    },
    ref,
  ) => {
    const calculateActualLength = (text: string) => {
      let modifiedString = text;
      suggestions.forEach((element) => {
        modifiedString = modifiedString.replaceAll(
          element.id,
          " ".repeat(element.count),
        );
      });
      return modifiedString.length;
    };

    const actualLength = calculateActualLength(value?.toString() || "");
    const isOverLimit = props.maxLength
      ? actualLength > props.maxLength
      : false;

    const inputHeight = rows * 20 + 20;

    return (
      <Field disabled={disabled}>
        {label && (
          <Label
            required={required}
            toolTipText={toolTipText}
            disabled={disabled}
          >
            {label}
          </Label>
        )}
        <div className="relative">
          <MentionsInput
            value={value?.toString() || ""}
            onChange={(e) => onChange?.(e.target.value)}
            onKeyDown={onKeyDown}
            style={{
              control: mentionInputStyle.control,
              height: `${inputHeight}px`,
              highlighter: {
                ...mentionInputStyle.highlighter,
                height: `${inputHeight}px`,
              },
              input: {
                ...mentionInputStyle.input,
                backgroundColor: disabled ? "#F9FAFB" : "white",
                border: isOverLimit
                  ? "1px solid #EF4444"
                  : mentionInputStyle.input.border,
                height: `${inputHeight}px`,
              },
              suggestions: {
                ...mentionInputStyle.suggestions,
                list: {
                  ...mentionInputStyle.suggestions.list,
                  position: "absolute" as const,
                },
              },
            }}
            className={clsx(
              "block w-full rounded-md text-gray-900 shadow-sm sm:text-sm",
              invalid || isOverLimit
                ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
              "focus:outline-none focus:ring-2 focus:ring-opacity-50",
              "disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500",
              props.maxLength && "pr-12",
              className,
            )}
            placeholder="Enter message. Use '{' for suggestions"
            disabled={disabled}
            inputRef={ref}
            allowSuggestionsAboveCursor
          >
            <Mention
              trigger="{"
              markup="{{.__id__}}"
              displayTransform={(id) => `{{.${id}}}`}
              data={suggestions}
            />
          </MentionsInput>
          {props.maxLength && (
            <span
              className={clsx(
                "absolute bottom-2 right-2 text-right text-xs",
                isOverLimit ? "text-red-500" : "text-gray-400",
              )}
            >
              {actualLength}/{props.maxLength}
            </span>
          )}
        </div>
      </Field>
    );
  },
);

TextAreaWithMentions.displayName = "TextAreaWithMentions";

export default TextAreaWithMentions;
